import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { createUser, updateUser, fetchuserdetails, resetStatus } from "./usersSlice";
import countrycode from "../../data/countrycode.json";
import toast from "react-hot-toast";

const Spinner = ({ size = 18 }) => (
  <svg className="animate-spin" width={size} height={size} viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
  </svg>
);

const UserForm = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { singleUser, status, formStatus } = useSelector((state) => state.users);
  const isLoading = formStatus === "loading";
  const isFetchingUser = isEdit && isLoading && !singleUser;
  const showSkeleton = isEdit && status === "loading" && !singleUser;
  const fieldRefs = useRef({});
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    title: "",
    name: "",
    lname: "",
    email: "",
    confirmEmail: "",
    password: "",
    password_confirmation: "",
    nationality: "",
    countrycode: "91",
    mobile: "",
    UserType: 7,
  });

  useEffect(() => {
    if (!isEdit) {
      dispatch(resetStatus());
    }
  }, [isEdit, dispatch]);
  useEffect(() => {
    if (isEdit) dispatch(fetchuserdetails(id));
  }, [id]);

  useEffect(() => {
    if (isEdit && singleUser) {
      setFormData({
        title: singleUser.title || "",
        name: singleUser.fname || "",
        lname: singleUser.lname || "",
        email: singleUser.email || "",
        confirmEmail: singleUser.email || "",
        nationality: singleUser.nationality || "",
        countrycode: singleUser.countrycode || "91",
        mobile: singleUser.mobile || "",
        password: "",
        password_confirmation: "",
        UserType: singleUser.UserType || 7,
      });
    }
  }, [singleUser]);

  const inputClass = (field) =>
    `w-full px-3 py-2 rounded-lg border transition focus:ring-2 ${
      errors[field] ? "border-red-500 focus:ring-red-400" : "border-gray-300 focus:ring-blue-500"
    }`;

  const scrollToError = (field) => {
    fieldRefs.current[field]?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const validate = () => {
    const e = {};
    if (!formData.title) e.title = "Title is required";
    if (!formData.name) e.name = "First name is required";
    if (!formData.lname) e.lname = "Last name is required";
    if (!formData.email) e.email = "Email is required";

    if (!isEdit) {
      // if (!formData.confirmEmail) e.confirmEmail = "Confirm email required";
      // if (formData.email !== formData.confirmEmail) e.confirmEmail = "Emails do not match";
      if (!formData.password) e.password = "Password required";
      if (formData.password !== formData.password_confirmation) e.password_confirmation = "Passwords do not match";
    }

    setErrors(e);
    if (Object.keys(e).length) {
      scrollToError(Object.keys(e)[0]);
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    if (!validate()) return;

    let payload = { ...formData };

    if (isEdit) {
      delete payload.password;
      delete payload.password_confirmation;
      const res = await dispatch(updateUser({ id, userData: payload }));
      return handleApiResponse(res, true);
    }

    const res = await dispatch(createUser(payload));
    handleApiResponse(res, false);
  };

  const handleApiResponse = (result, isEdit) => {
    const api = result.payload;

    if (api?.status?.success === false) {
      setErrors(api.error || {});
      Object.values(api.error || {})
        .flat()
        .forEach((msg) => toast.error(msg));
      scrollToError(Object.keys(api.error || {})[0]);
      return;
    }

    toast.success(isEdit ? "User updated successfully" : "User created successfully");
    navigate("/users");
  };

  if (showSkeleton) {
    return (
      <div className="max-w-3xl mx-auto mt-10 bg-white rounded-xl shadow p-8 animate-pulse space-y-4">
        <div className="h-6 bg-gray-200 w-1/3 rounded" />
        <div className="h-10 bg-gray-200 rounded" />
        <div className="h-10 bg-gray-200 rounded" />
        <div className="h-10 bg-gray-200 rounded" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto mt-10 bg-white shadow-xl rounded-xl">
      {/* Header */}
      <div className="border-b px-8 py-5">
        <h2 className="text-xl font-semibold">{isEdit ? "Edit User" : "Create New User"}</h2>
        <p className="text-sm text-gray-500 mt-1">{isEdit ? "Update user details" : "Fill in user information"}</p>
      </div>

      <form onSubmit={handleSubmit} className="p-8 space-y-10">
        {/* ===== Personal Info ===== */}
        <section>
          <h3 className="text-sm font-semibold text-gray-700 mb-4">PERSONAL INFORMATION</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div ref={(el) => (fieldRefs.current.title = el)}>
              <label className="text-sm">Title</label>
              <select
                disabled={isLoading}
                className={inputClass("title")}
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              >
                <option value="">Select</option>
                <option>Mr</option>
                <option>Ms</option>
                <option>Mrs</option>
              </select>
              {errors.title && <p className="text-red-500 text-xs">{errors.title}</p>}
            </div>

            <div ref={(el) => (fieldRefs.current.name = el)}>
              <label className="text-sm">First Name</label>
              <input
                disabled={isLoading}
                className={inputClass("name")}
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
              {errors.name && <p className="text-red-500 text-xs">{errors.name}</p>}
            </div>

            <div ref={(el) => (fieldRefs.current.lname = el)}>
              <label className="text-sm">Last Name</label>
              <input
                disabled={isLoading}
                className={inputClass("lname")}
                value={formData.lname}
                onChange={(e) => setFormData({ ...formData, lname: e.target.value })}
              />
              {errors.lname && <p className="text-red-500 text-xs">{errors.lname}</p>}
            </div>
          </div>
        </section>

        {/* ===== Contact Info ===== */}
        <section>
          <h3 className="text-sm font-semibold text-gray-700 mb-4">CONTACT INFORMATION</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div ref={(el) => (fieldRefs.current.countrycode = el)}>
              <label className="text-sm">Country</label>
              <select
                disabled={isLoading}
                className={inputClass("countrycode")}
                value={formData.countrycode}
                onChange={(e) => {
                  const c = countrycode.find((x) => x.dial_code === e.target.value);
                  setFormData({
                    ...formData,
                    countrycode: e.target.value,
                    nationality: c?.name || "",
                  });
                }}
              >
                <option value="">Select Country</option>
                {countrycode.map((c) => (
                  <option key={c.code} value={c.dial_code}>
                    {c.name} (+{c.dial_code})
                  </option>
                ))}
              </select>
            </div>

            <div ref={(el) => (fieldRefs.current.mobile = el)}>
              <label className="text-sm">Mobile</label>
              <input
                disabled={isLoading}
                className={inputClass("mobile")}
                value={formData.mobile}
                onChange={(e) => setFormData({ ...formData, mobile: e.target.value.replace(/\D/g, "") })}
                placeholder="Mobile number"
              />
            </div>

            <div ref={(el) => (fieldRefs.current.email = el)}>
              <label className="text-sm">Email</label>
              <input
                disabled={isLoading || isEdit}
                type="email"
                className={inputClass("email")}
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
              {errors.email && <p className="text-red-500 text-xs">{errors.email}</p>}
            </div>
          </div>
        </section>

        {/* ===== Security ===== */}
        {!isEdit && (
          <section>
            <h3 className="text-sm font-semibold text-gray-700 mb-4">SECURITY</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div ref={(el) => (fieldRefs.current.password = el)}>
                <label className="text-sm">Password</label>
                <input
                  type="password"
                  disabled={isLoading}
                  className={inputClass("password")}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                />
                {errors.password && <p className="text-red-500 text-xs">{errors.password}</p>}
              </div>

              <div ref={(el) => (fieldRefs.current.password_confirmation = el)}>
                <label className="text-sm">Confirm Password</label>
                <input
                  type="password"
                  disabled={isLoading}
                  className={inputClass("password_confirmation")}
                  value={formData.password_confirmation}
                  onChange={(e) => setFormData({ ...formData, password_confirmation: e.target.value })}
                />
                {errors.password_confirmation && <p className="text-red-500 text-xs">{errors.password_confirmation}</p>}
              </div>
            </div>
          </section>
        )}

        {/* ===== Action Bar ===== */}
        <div className="flex justify-end gap-3 pt-6 border-t">
          <button
            type="button"
            onClick={() => navigate("/users")}
            className="px-5 py-2 rounded-lg border text-gray-600 hover:bg-gray-50"
          >
            Cancel
          </button>

          <button
            type="submit"
            disabled={isLoading}
            className={`px-6 py-2 rounded-lg flex items-center gap-2 text-white
              ${isLoading ? "bg-blue-400" : "bg-blue-600 hover:bg-blue-700"}`}
          >
            {isLoading && <Spinner />}
            {isEdit ? "Update User" : "Create User"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default UserForm;
